import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from './icons';
import { useGA4 } from '../hooks/useGA4';

interface ImageSliderProps {
  images: {
    src: string;
    alt: string;
    beforeLabel?: string;
    afterLabel?: string;
  }[];
  className?: string;
}

function ImageSlider({ images, className = '' }: ImageSliderProps): React.JSX.Element {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const { trackEvent } = useGA4();

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      trackEvent('image_slider_navigation', {
        action: 'dot_click',
        slide_index: index,
        total_slides: images.length
      });
      emblaApi && emblaApi.scrollTo(index);
    },
    [emblaApi, trackEvent, images.length]
  );

  const scrollPrev = useCallback(
    () => {
      trackEvent('image_slider_navigation', {
        action: 'previous',
        current_slide: selectedIndex,
        total_slides: images.length
      });
      emblaApi && emblaApi.scrollPrev();
    },
    [emblaApi, trackEvent, selectedIndex, images.length]
  );

  const scrollNext = useCallback(
    () => {
      trackEvent('image_slider_navigation', {
        action: 'next',
        current_slide: selectedIndex,
        total_slides: images.length
      });
      emblaApi && emblaApi.scrollNext();
    },
    [emblaApi, trackEvent, selectedIndex, images.length]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section className={`image-slider-section ${className}`}>
      <div className="image-slider-container">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {images.map((image, index) => (
              <div className="embla__slide" key={index}>
                <div className="slide-content">
                  <img 
                    src={image.src} 
                    alt={image.alt} 
                    className="embla__slide__img" 
                  />
                  {(image.beforeLabel || image.afterLabel) && (
                    <div className="slide-labels">
                      {image.beforeLabel && (
                        <span className="before-label">{image.beforeLabel}</span>
                      )}
                      {image.afterLabel && (
                        <span className="after-label">{image.afterLabel}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          className="embla__button embla__button--prev" 
          onClick={scrollPrev}
          aria-label="Previous slide"
        >
          <ArrowLeft size={24} />
        </button>
        <button 
          className="embla__button embla__button--next" 
          onClick={scrollNext}
          aria-label="Next slide"
        >
          <ArrowRight size={24} />
        </button>

        {/* Dots Navigation */}
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`embla__dot ${index === selectedIndex ? 'embla__dot--selected' : ''}`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default ImageSlider; 